import { apiRequest, clearAuthTokens, getRefreshToken, hasActiveSession, setAuthTokens, toQueryString, unwrap } from '../lib/apiClient.js'

export { hasActiveSession }

const get = (path, params, options) => apiRequest(`${path}${toQueryString(params)}`, options)
const post = (path, body, options) => apiRequest(path, { method: 'POST', body, ...options })
const patch = (path, body, options) => apiRequest(path, { method: 'PATCH', body, ...options })
const put = (path, body, options) => apiRequest(path, { method: 'PUT', body, ...options })
const del = (path, options) => apiRequest(path, { method: 'DELETE', ...options })

function moneyParts(value) {
  const [whole = '0', fraction = ''] = String(value ?? '0').split('.')
  return BigInt(whole || '0') * 100n + BigInt(fraction.padEnd(2, '0').slice(0, 2) || '0')
}

function sumMoney(values) {
  const total = values.reduce((sum, value) => sum + moneyParts(value), 0n)
  return `${total / 100n}.${String(total % 100n).padStart(2, '0')}`
}

function sessionFromPayload(payload) {
  const data = unwrap(payload, {})
  const accessToken = data.accessToken ?? data.access_token
  const refreshToken = data.refreshToken ?? data.refresh_token
  setAuthTokens({ accessToken, refreshToken })
  return data.user ?? data
}

export const authApi = {
  async me(signal) {
    return unwrap(await get('/me', null, { signal }))
  },
  async login(username, password) {
    return sessionFromPayload(await post('/auth/login', { username, password }))
  },
  async register(payload) {
    return sessionFromPayload(await post('/auth/register', payload))
  },
  async google(credential) {
    return sessionFromPayload(await post('/auth/google', { credential }))
  },
  async email(email, role) {
    return sessionFromPayload(await post('/auth/email', { email, role }))
  },
  async chooseRole(role) {
    return sessionFromPayload(await post('/auth/role', { role }))
  },
  async completeOnboarding(payload) {
    return sessionFromPayload(await post('/auth/onboarding', payload))
  },
  async logout() {
    try {
      const refreshToken = getRefreshToken()
      await post('/auth/logout', refreshToken ? { refreshToken } : {})
    } finally {
      clearAuthTokens()
    }
  },
}

export const platformBankApi = {
  async listActive(signal) {
    return unwrap(await get('/platform/bank-accounts', null, { signal }))
  },
  async listAll(signal) {
    return unwrap(await get('/admin/bank-accounts', null, { signal }))
  },
  async create(data) {
    return unwrap(await post('/admin/bank-accounts', data))
  },
  async update(id, data) {
    return unwrap(await patch(`/admin/bank-accounts/${id}`, data))
  },
  async remove(id) {
    return unwrap(await apiRequest(`/admin/bank-accounts/${id}`, { method: 'DELETE' }))
  },
}

export const platformSettingsApi = {
  async getPublicSettings(signal) {
    return unwrap(await get('/platform/settings', null, { signal }))
  },
  async getAdminSettings(signal) {
    return unwrap(await get('/admin/settings', null, { signal }))
  },
  async updateAdminSettings(data) {
    return unwrap(await patch('/admin/settings', data))
  },
  async sendTestEmail(data) {
    return unwrap(await post('/admin/settings/test-email', data))
  },
  async getFonnteStatus(signal) {
    return unwrap(await get('/admin/settings/fonnte-status', null, { signal }))
  },
}

export const broadcastApi = {
  async listHistory(params, signal) {
    return get('/admin/broadcasts', params, { signal })
  },
  async getDetail(id, signal) {
    return unwrap(await get(`/admin/broadcasts/${id}`, null, { signal }))
  },
  async listCandidates(params, signal) {
    return get('/admin/broadcasts/candidates', params, { signal })
  },
  async sendBroadcast(data) {
    return unwrap(await post('/admin/broadcasts/send', data))
  },
  async getFonnteStatus(signal) {
    return unwrap(await get('/admin/broadcasts/fonnte-status', null, { signal }))
  },
}

export const brandWalletApi = {
  async getWallet(signal) {
    return unwrap(await get('/brands/me/wallet', null, { signal }))
  },
  async createTopUpRequest(data) {
    return unwrap(await post('/brands/me/topup-requests', data))
  },
  async listTopUpRequests(signal) {
    return unwrap(await get('/brands/me/topup-requests', null, { signal }))
  },
  async listTransactions(params, signal) {
    return unwrap(await get('/brands/me/wallet/transactions', params, { signal }), [])
  },
}

export const adminBrandWalletApi = {
  async listWallets(signal) {
    return unwrap(await get('/admin/brands/wallets', null, { signal }))
  },
  async listTopUpRequests(signal) {
    return unwrap(await get('/admin/brands/topup-requests', null, { signal }))
  },
  async listTransactions(params, signal) {
    return unwrap(await get('/admin/brands/transactions', params, { signal }), [])
  },
  async listExpiredRefunds(params, signal) {
    return unwrap(await get('/admin/brands/expired-refunds', params, { signal }), [])
  },
  async forceRefundCampaign(campaignId, reason) {
    return unwrap(await post(`/admin/brands/campaigns/${encodeURIComponent(campaignId)}/force-refund`, { reason }))
  },
  async approveTopUpRequest(id) {
    return unwrap(await post(`/admin/brands/topup-requests/${encodeURIComponent(id)}/approve`, {}))
  },
  async rejectTopUpRequest(id) {
    return unwrap(await post(`/admin/brands/topup-requests/${encodeURIComponent(id)}/reject`, {}))
  },
  async creditWallet(brandId, amount, reason) {
    return unwrap(await post(`/admin/brands/wallets/${encodeURIComponent(brandId)}/credit`, { amount, reason }))
  },
  async debitWallet(brandId, amount, reason) {
    return unwrap(await post(`/admin/brands/wallets/${encodeURIComponent(brandId)}/debit`, { amount, reason }))
  },
  async toggleFreeze(brandId, payload = {}) {
    return unwrap(await post(`/admin/brands/wallets/${encodeURIComponent(brandId)}/freeze`, payload))
  },
  async freezeWallet(brandId, reason) {
    return unwrap(await post(`/admin/brands/wallets/${encodeURIComponent(brandId)}/freeze`, { isFrozen: true, freezeReason: reason }))
  },
  async unfreezeWallet(brandId) {
    return unwrap(await post(`/admin/brands/wallets/${encodeURIComponent(brandId)}/freeze`, { isFrozen: false }))
  },
}

export const adminTopUpRequestApi = {
  async listRequests(params, signal) {
    return get('/admin/brands/topup-requests', params, { signal })
  },
  async approveRequest(id, input = {}) {
    return unwrap(await post(`/admin/brands/topup-requests/${encodeURIComponent(id)}/approve`, input))
  },
  async rejectRequest(id, input = {}) {
    return unwrap(await post(`/admin/brands/topup-requests/${encodeURIComponent(id)}/reject`, input))
  },
}

export const campaignApi = {
  async list(params, signal) {
    return get('/campaigns', params, { signal })
  },
  async featured(signal) {
    return get('/campaigns', { page: 1, pageSize: 6, sort: 'newest' }, { signal })
  },
  async detail(publicId, signal) {
    return unwrap(await get(`/campaigns/${encodeURIComponent(publicId)}`, null, { signal }))
  },
  async get(publicId, signal) {
    return unwrap(await get(`/campaigns/${encodeURIComponent(publicId)}`, null, { signal }))
  },
  async apply(publicId) {
    return unwrap(await post(`/campaigns/${encodeURIComponent(publicId)}/applications`, {}))
  },
}

export const categoryApi = {
  async list(signal) {
    return unwrap(await get('/categories', { page: 1, pageSize: 100 }, { signal }), [])
  },
}

export const creatorApi = {
  async dashboard(signal) {
    const [walletRes, applicationsRes] = await Promise.all([
      get('/me/wallet', null, { signal }).catch(() => ({})),
      get('/me/applications', { page: 1, pageSize: 50 }, { signal }).catch(() => ({ data: [], items: [] })),
    ])
    const walletData = unwrap(walletRes, {})
    const applicationList = unwrap(applicationsRes, [])
    const applicationItems = Array.isArray(applicationList) ? applicationList : (applicationsRes?.items || applicationsRes?.data || [])

    // Active jobs: APPROVED, IN_PROGRESS, REVISION_REQUIRED (creator is currently working on or needs to submit link)
    const activeCount = applicationItems.filter((item) =>
      ['APPROVED', 'IN_PROGRESS', 'REVISION_REQUIRED'].includes(item.status)
    ).length

    // Under Review: submitted content waiting for admin validation, or pending application approval
    const underReviewCount = applicationItems.filter((item) =>
      ['SUBMITTED', 'UNDER_REVIEW', 'PENDING'].includes(item.status)
    ).length

    return {
      availableBalance: walletData.availableBalance ?? '0.00',
      totalEarning: walletData.totalEarning ?? '0.00',
      activeApplications: activeCount,
      pendingSubmissions: underReviewCount,
      recentApplications: applicationItems,
    }
  },
  async profile(signal) {
    const res = await get('/me', null, { signal })
    const data = unwrap(res, {})
    return data.profile || data.creatorProfile || data
  },
  async updateProfile(input) {
    return unwrap(await patch('/me/creator-profile', input))
  },
  async applications(params, signal) {
    return get('/me/applications', params, { signal })
  },
  async application(id, signal) {
    return unwrap(await get(`/me/applications/${encodeURIComponent(id)}`, null, { signal }))
  },
  async submit(applicationId, input) {
    const headers = {}
    if (input?.idempotencyKey) headers['idempotency-key'] = input.idempotencyKey
    return unwrap(await post(`/applications/${encodeURIComponent(applicationId)}/submission`, input, { headers }))
  },
  async submitContent(applicationId, input) {
    const headers = {}
    if (input?.idempotencyKey) headers['idempotency-key'] = input.idempotencyKey
    return unwrap(await post(`/applications/${encodeURIComponent(applicationId)}/submission`, input, { headers }))
  },
  async wallet(signal) {
    return unwrap(await get('/me/wallet', null, { signal }), {
      availableBalance: '0.00',
      totalEarning: '0.00',
      pendingWithdrawal: '0.00',
      reservedEarnings: '0.00',
    })
  },
  async getWallet(signal) {
    return this.wallet(signal)
  },
  async ledger(params, signal) {
    return get('/me/ledger', params, { signal })
  },
  async earnings(params, signal) {
    return get('/me/earnings', params, { signal })
  },
  async withdrawals(params, signal) {
    return get('/me/withdrawals', params, { signal })
  },
  async createWithdrawal(input) {
    const headers = {}
    if (input?.idempotencyKey) headers['idempotency-key'] = input.idempotencyKey
    return unwrap(await post('/withdrawals', input, { headers }))
  },
  async requestWithdrawal(input) {
    const headers = {}
    if (input?.idempotencyKey) headers['idempotency-key'] = input.idempotencyKey
    return unwrap(await post('/withdrawals', input, { headers }))
  },
  async bankAccount(signal) {
    return unwrap(await get('/me/bank-account', null, { signal }))
  },
  async saveBankAccount(input) {
    return unwrap(await post('/me/bank-account', input))
  },
  async cancelApplication(applicationId) {
    return unwrap(await post(`/me/applications/${encodeURIComponent(applicationId)}/cancel`, {}))
  },
}

export const brandApi = {
  async dashboard(signal) {
    const [payload, applicationsPayload] = await Promise.all([
      get('/brands/me/campaigns', { page: 1, pageSize: 50 }, { signal }),
      get('/brands/me/applications', { page: 1, pageSize: 1 }, { signal }),
    ])
    const items = unwrap(payload, [])
    const totalValidViews = items.reduce((sum, item) => sum + Number(item.validViews ?? item.valid_views ?? 0), 0)
    return {
      activeCampaigns: items.filter((item) => item.status === 'ACTIVE').length,
      totalCampaigns: payload?.meta?.total ?? items.length,
      validViews: totalValidViews,
      totalBudget: sumMoney(items.map((item) => item.budget?.total)),
      budgetReserved: sumMoney(items.map((item) => item.budget?.reserved)),
      budgetSpent: sumMoney(items.map((item) => item.budget?.spent)),
      budgetAvailable: sumMoney(items.map((item) => item.budget?.available)),
      applicationCount: applicationsPayload?.meta?.total ?? 0,
      recentCampaigns: items.slice(0, 5),
    }
  },
  async profile(signal) {
    return unwrap(await get('/brands/me', null, { signal }), {})
  },
  async updateProfile(input) {
    return unwrap(await patch('/brands/me', input))
  },
  async campaigns(params, signal) {
    return get('/brands/me/campaigns', params, { signal })
  },
  async campaign(id, signal) {
    return unwrap(await get(`/brands/me/campaigns/${encodeURIComponent(id)}`, null, { signal }))
  },
  async createCampaign(input) {
    return unwrap(await post('/campaigns', input))
  },
  async updateCampaign(id, input) {
    return unwrap(await patch(`/campaigns/${encodeURIComponent(id)}`, input))
  },
  async deleteCampaign(id) {
    return unwrap(await apiRequest(`/brands/me/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' }))
  },
  async topupCampaignBudget(id, input) {
    return unwrap(await post(`/brands/me/campaigns/${encodeURIComponent(id)}/topup-budget`, input))
  },
  async completeCampaign(id) {
    return unwrap(await post(`/brands/me/campaigns/${encodeURIComponent(id)}/complete`, {}))
  },
  async submitForReview(id) {
    return unwrap(await post(`/campaigns/${encodeURIComponent(id)}/submit-review`, {}))
  },
  async applications(params, signal) {
    return get('/brands/me/applications', params, { signal })
  },
  async finance(params, signal) {
    const payload = await get('/brands/me/campaigns', params, { signal })
    const items = unwrap(payload, [])
    return {
      data: items,
      meta: {
        ...payload.meta,
        summary: {
          total: sumMoney(items.map((item) => item.budget?.total)),
          reserved: sumMoney(items.map((item) => item.budget?.reserved)),
          spent: sumMoney(items.map((item) => item.budget?.spent)),
          available: sumMoney(items.map((item) => item.budget?.available)),
        },
      },
    }
  },
}

export const adminApi = {
  async dashboard(signal) {
    const safeGet = (path, params) =>
      get(path, params, { signal }).catch((err) =>
        err?.status === 403 ? { meta: { total: 0 }, data: [] } : Promise.reject(err)
      )

    const [reviews, active, submitted, underReview, withdrawals, creators, brands] = await Promise.all([
      safeGet('/admin/campaigns', { page: 1, pageSize: 1, status: 'PENDING_REVIEW' }),
      safeGet('/admin/campaigns', { page: 1, pageSize: 1, status: 'ACTIVE' }),
      safeGet('/admin/submissions', { page: 1, pageSize: 1, status: 'SUBMITTED' }),
      safeGet('/admin/submissions', { page: 1, pageSize: 1, status: 'UNDER_REVIEW' }),
      safeGet('/admin/withdrawals', { page: 1, pageSize: 20, status: 'PENDING' }),
      safeGet('/admin/creators', { page: 1, pageSize: 1 }),
      safeGet('/admin/brands', { page: 1, pageSize: 1 }),
    ])
    const withdrawalItems = unwrap(withdrawals, [])
    return {
      pendingCampaigns: reviews.meta?.total ?? 0,
      pendingSubmissions: (submitted.meta?.total ?? 0) + (underReview.meta?.total ?? 0),
      pendingWithdrawals: withdrawals.meta?.total ?? 0,
      activeCampaigns: active.meta?.total ?? 0,
      pendingWithdrawalAmount: sumMoney(withdrawalItems.map((item) => item.amount)),
      creatorCount: creators.meta?.total ?? 0,
      brandCount: brands.meta?.total ?? 0,
      activity: [],
    }
  },
  async campaigns(params, signal) {
    return get('/admin/campaigns', params, { signal })
  },
  async campaign(id, signal) {
    return unwrap(await get(`/admin/campaigns/${encodeURIComponent(id)}`, null, { signal }))
  },
  async reviewCampaign(id, input) {
    return unwrap(await post(`/admin/campaigns/${encodeURIComponent(id)}/review`, input))
  },
  async setCampaignRate(id, input) {
    return unwrap(await patch(`/admin/campaigns/${encodeURIComponent(id)}/rate`, input))
  },
  async publishCampaign(id) {
    return unwrap(await post(`/admin/campaigns/${encodeURIComponent(id)}/publish`, {}))
  },
  async updateCampaign(id, input) {
    return unwrap(await patch(`/admin/campaigns/${encodeURIComponent(id)}`, input))
  },
  async deleteCampaign(id) {
    return unwrap(await apiRequest(`/admin/campaigns/${encodeURIComponent(id)}`, { method: 'DELETE' }))
  },
  async completeCampaign(id) {
    return unwrap(await post(`/admin/campaigns/${encodeURIComponent(id)}/complete`, {}))
  },
  async reviewCreator(publicId, input) {
    return unwrap(await patch(`/admin/creators/${encodeURIComponent(publicId)}/review`, input))
  },
  async creators(params, signal) {
    return get('/admin/creators', params, { signal })
  },
  async brands(params, signal) {
    return get('/admin/brands', params, { signal })
  },
  async submissions(params, signal) {
    return get('/admin/submissions', params, { signal })
  },
  async approveApplication(id) {
    return unwrap(await post(`/admin/applications/${encodeURIComponent(id)}/approve`, {}))
  },
  async rejectApplication(id) {
    return unwrap(await post(`/admin/applications/${encodeURIComponent(id)}/reject`, {}))
  },
  async cancelApplication(id) {
    return unwrap(await post(`/admin/applications/${encodeURIComponent(id)}/cancel`, {}))
  },
  async validateSubmission(id, input) {
    const { idempotencyKey, ...body } = input
    return unwrap(await post(`/admin/submissions/${encodeURIComponent(id)}/validate`, body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }))
  },
  async requestSubmissionRevision(id, input) {
    return unwrap(await post(`/admin/submissions/${encodeURIComponent(id)}/request-revision`, input))
  },
  async withdrawals(params, signal) {
    return get('/admin/withdrawals', params, { signal })
  },
  async processWithdrawal(id, input = {}) {
    return unwrap(await post(`/admin/withdrawals/${encodeURIComponent(id)}/process`, input))
  },
  async markWithdrawalPaid(id, input = {}) {
    return unwrap(await post(`/admin/withdrawals/${encodeURIComponent(id)}/mark-paid`, input))
  },
  async rejectWithdrawal(id, input) {
    return unwrap(await post(`/admin/withdrawals/${encodeURIComponent(id)}/reject`, input))
  },
  async revealWithdrawalAccount(id) {
    return unwrap(await get(`/admin/withdrawals/${encodeURIComponent(id)}/bank-account`))
  },
  async correctValidation(id, input) {
    const { idempotencyKey, ...body } = input
    return unwrap(await post(`/admin/submissions/${encodeURIComponent(id)}/correct-validation`, body, {
      headers: { 'Idempotency-Key': idempotencyKey },
    }))
  },
  async list(resource, params, signal) {
    return get(`/admin/${resource}`, params, { signal })
  },
  async createCategory(input) {
    return unwrap(await post('/admin/categories', input))
  },
  async updateCategory(id, input) {
    return unwrap(await patch(`/admin/categories/${encodeURIComponent(id)}`, input))
  },
  async suspendUser(publicId, reason) {
    return unwrap(await post(`/admin/users/${encodeURIComponent(publicId)}/suspend`, { reason }))
  },
  async reactivateUser(publicId) {
    return unwrap(await post(`/admin/users/${encodeURIComponent(publicId)}/reactivate`, {}))
  },
  async resetUserPassword(publicId, newPassword) {
    return unwrap(await post(`/admin/users/${encodeURIComponent(publicId)}/reset-password`, { newPassword }))
  },
  async deleteUser(publicId) {
    return unwrap(await apiRequest(`/admin/users/${encodeURIComponent(publicId)}`, { method: 'DELETE' }))
  },
  async updateBrandVerification(publicId, status) {
    return unwrap(await patch(`/admin/brands/${encodeURIComponent(publicId)}/verification`, { status }))
  },
}

export const superAdminApi = {
  async listAdmins(params, signal) {
    return get('/admin/superadmin/admins', params, { signal })
  },
  async createAdmin(input) {
    return unwrap(await post('/admin/superadmin/admins', input))
  },
  async suspendAdmin(publicId, reason) {
    return unwrap(await post(`/admin/superadmin/admins/${encodeURIComponent(publicId)}/suspend`, { reason }))
  },
  async reactivateAdmin(publicId) {
    return unwrap(await post(`/admin/superadmin/admins/${encodeURIComponent(publicId)}/reactivate`, {}))
  },
  async resetAdminPassword(publicId, newPassword) {
    return unwrap(await post(`/admin/superadmin/admins/${encodeURIComponent(publicId)}/reset-password`, { newPassword }))
  },
  async updateAdmin(publicId, input) {
    return unwrap(await put(`/admin/superadmin/admins/${encodeURIComponent(publicId)}`, input))
  },
  async deleteAdmin(publicId) {
    return unwrap(await apiRequest(`/admin/superadmin/admins/${encodeURIComponent(publicId)}`, { method: 'DELETE' }))
  },
}

export const uploadApi = {
  async image(file, category = 'images') {
    const body = new FormData()
    body.append('image', file)
    const query = category ? `?category=${encodeURIComponent(category)}` : ''
    return unwrap(await post(`/uploads/images${query}`, body))
  },
  async verification(file) {
    const body = new FormData()
    body.append('image', file)
    return unwrap(await post('/uploads/verification-images', body))
  },
}

export const rbacApi = {
  async permissions(signal) {
    return unwrap(await get('/admin/rbac/permissions', null, { signal }))
  },
  async roles(signal) {
    return unwrap(await get('/admin/rbac/roles', null, { signal }))
  },
  async createRole(input) {
    return unwrap(await post('/admin/rbac/roles', input))
  },
  async updateRole(id, input) {
    return unwrap(await patch(`/admin/rbac/roles/${encodeURIComponent(id)}`, input))
  },
  async deleteRole(id) {
    return unwrap(await del(`/admin/rbac/roles/${encodeURIComponent(id)}`))
  },
  async assignUserRoles(publicId, roleCodes) {
    return unwrap(await put(`/admin/rbac/users/${encodeURIComponent(publicId)}/roles`, { roleCodes }))
  },
}

export const reportApi = {
  async unifiedLedger(params, signal) {
    return get('/admin/ledger/unified', params, { signal })
  },
  async revenue(params, signal) {
    return unwrap(await get('/admin/reports/revenue', params, { signal }))
  },
  async cashflow(params, signal) {
    return unwrap(await get('/admin/reports/cashflow', params, { signal }))
  },
  async campaigns(params, signal) {
    return get('/admin/reports/campaigns', params, { signal })
  },
  async executive(params, signal) {
    return unwrap(await get('/admin/reports/executive', params, { signal }))
  },
}


